import { useCallback, useEffect, useRef, useState } from 'react';

const useNaverDiscoverLocation = ({ mapInstanceRef, suppressAutoRecenterUntilRef, suppressFitBoundsUntilRef, onNotify }) => {
  const [userLocation, setUserLocation] = useState(null);
  const [isLocating, setIsLocating] = useState(false);
  const autoLocateRef = useRef(false);

  const handleUseCurrentLocation = useCallback(async () => {
    if (typeof navigator === 'undefined' || !navigator.geolocation) {
      onNotify?.({ title: '위치 기능 불가', message: '이 브라우저에서는 위치 기능을 사용할 수 없습니다.' }) ||
        alert('이 브라우저에서는 위치 기능을 사용할 수 없습니다.');
      return;
    }

    setIsLocating(true);
    try {
      const getPosition = (options) =>
        new Promise((resolve, reject) => {
          navigator.geolocation.getCurrentPosition(resolve, reject, options);
        });

      let position;
      try {
        position = await getPosition({
          enableHighAccuracy: true,
          timeout: 10000,
          maximumAge: 30000,
        });
      } catch (err) {
        if (err?.code === 2) {
          position = await getPosition({
            enableHighAccuracy: false,
            timeout: 15000,
            maximumAge: 0,
          });
        } else {
          throw err;
        }
      }

      const lat = position?.coords?.latitude;
      const lng = position?.coords?.longitude;
      if (typeof lat !== 'number' || typeof lng !== 'number') {
        onNotify?.({ title: '위치 확인 실패', message: '현재 위치를 확인할 수 없습니다.' }) ||
          alert('현재 위치를 확인할 수 없습니다.');
        return;
      }

      const next = { lat, lng };
      setUserLocation(next);

      if (window.naver?.maps && mapInstanceRef?.current) {
        const map = mapInstanceRef.current;
        const target = new window.naver.maps.LatLng(lat, lng);
        try {
          if (suppressFitBoundsUntilRef) {
            suppressFitBoundsUntilRef.current = Date.now() + 15000;
          }
          suppressAutoRecenterUntilRef.current = Date.now() + 15000;

          const desiredZoom = 15;
          const currentZoom = typeof map.getZoom === 'function' ? map.getZoom() : null;
          const nextZoom = Number.isFinite(currentZoom) ? Math.max(desiredZoom, currentZoom) : desiredZoom;

          if (typeof map.morph === 'function') {
            map.morph(target, nextZoom);
          } else if (typeof map.panTo === 'function') {
            map.panTo(target);
            map.setZoom(nextZoom);
          } else {
            map.setCenter(target);
            map.setZoom(nextZoom);
          }
        } catch {
          map.setCenter(target);
          map.setZoom(15);
        }
      }
    } catch (e) {
      console.group('[NaverDiscover] geolocation error');
      console.error(e);
      console.groupEnd();

      const code = e?.code;
      if (code === 1) {
        onNotify?.({
          title: '위치 권한 거부',
          message: '브라우저/OS 설정에서 이 사이트의 위치 권한을 허용해주세요.',
        }) || alert('위치 권한이 거부되었습니다. 브라우저/OS 설정에서 이 사이트의 위치 권한을 허용해주세요.');
        return;
      }
      if (code === 2) {
        onNotify?.({
          title: '위치 정보 없음',
          message: 'OS 위치 서비스가 켜져있는지, Wi‑Fi/네트워크 상태를 확인해주세요.',
        }) || alert('위치 정보를 확인할 수 없습니다. OS 위치 서비스가 켜져있는지, Wi‑Fi/네트워크 상태를 확인해주세요.');
        return;
      }
      if (code === 3) {
        onNotify?.({ title: '위치 확인 지연', message: '위치 확인 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.' }) ||
          alert('위치 확인 시간이 초과되었습니다. 잠시 후 다시 시도해주세요.');
        return;
      }

      const isLocalhost = typeof window !== 'undefined' && /^(localhost|127\.0\.0\.1)$/i.test(window.location.hostname);
      const isSecure = typeof window !== 'undefined' && window.isSecureContext;
      if (!isSecure && !isLocalhost) {
        onNotify?.({ title: '보안 환경 필요', message: '현재 위치 기능은 HTTPS 환경에서만 동작합니다.' }) ||
          alert('현재 위치 기능은 HTTPS 환경에서만 동작합니다.');
        return;
      }

      onNotify?.({ title: '위치 확인 실패', message: '현재 위치를 가져오지 못했습니다.' }) ||
        alert('현재 위치를 가져오지 못했습니다.');
    } finally {
      setIsLocating(false);
    }
  }, [mapInstanceRef, suppressAutoRecenterUntilRef, suppressFitBoundsUntilRef]);

  useEffect(() => {
    if (autoLocateRef.current) return;
    if (userLocation) return;
    autoLocateRef.current = true;
    handleUseCurrentLocation();
  }, [handleUseCurrentLocation, userLocation]);

  return {
    userLocation,
    isLocating,
    setUserLocation,
    handleUseCurrentLocation,
  };
};

export default useNaverDiscoverLocation;
